import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../../services/api";
import "../LoginPage/LoginPage.css";
import LoadingFallback from "../../../components/common/LoadingFallback/LoadingFallback";
import Snowfall from "react-snowfall";

/**
 * GoogleAuthCallback
 *
 * Handles the redirect from the server after Google OAuth.
 *
 * Two possible scenarios:
 * 1. ?start2FA=true&email=...  → Show OTP input for 2FA verification
 * 2. ?error=...                → Show error message
 */
const GoogleAuthCallback = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing"); // processing, otp, success, error
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const error = searchParams.get("error");
    const token = searchParams.get("token");
    const adminData = searchParams.get("admin");
    const start2FA = searchParams.get("start2FA");
    const emailParam = searchParams.get("email");
    const nameParam = searchParams.get("name");

    if (error) {
      setStatus("error");
      setErrorMsg(decodeURIComponent(error));
      return;
    }

    if (token && adminData) {
      // Direct login (Bypassing 2FA)
      try {
        const admin = JSON.parse(decodeURIComponent(adminData));
        onLogin({ ...admin, token });
        setStatus("success");
        setTimeout(() => {
          window.location.href = "/dashboard";
        }, 500);
      } catch (err) {
        setStatus("error");
        setErrorMsg("Failed to process login data.");
      }
      return;
    }

    if (start2FA === "true" && emailParam) {
      // Re-added for backward compatibility, though server should skip it now
      setEmail(decodeURIComponent(emailParam));
      setUserName(nameParam ? decodeURIComponent(nameParam) : "");
      setStatus("otp");
      return;
    }

    // No valid params
    setStatus("error");
    setErrorMsg("No authentication data received.");
  }, [searchParams, onLogin]);

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      setErrorMsg("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      // Call the existing login-verify endpoint (same as normal login 2FA)
      const res = await api.verifyLoginOTP({ email, otp });

      if (res.data.success) {
        onLogin({ ...res.data.admin, token: res.data.token });
        setStatus("success");
        // Force full page reload to ensure App re-renders with logged-in state
        setTimeout(() => {
          window.location.href = "/dashboard";
        }, 500);
      }
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        "OTP verification failed. Please try again.";
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Snowfall color="#fff" snowflakeCount={100} />
      <div className="login-card" style={{ textAlign: "center" }}>
        {/* Processing state */}
        {status === "processing" && (
          <LoadingFallback message="Signing you in... Please wait while we complete your Google sign-in." />
        )}

        {/* OTP Verification state */}
        {status === "otp" && (
          <>
            <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>🔐</div>
            <h2
              style={{
                color: "var(--text-primary, #fff)",
                margin: "0 0 4px 0",
                fontSize: "1.5rem",
                fontWeight: 700,
              }}
            >
              Verify Your Identity
            </h2>
            <p
              style={{
                color: "var(--text-secondary, #94a3b8)",
                margin: "0 0 8px 0",
                fontSize: "0.9rem",
              }}
            >
              {userName ? `Welcome back, ${userName}!` : "Google verified ✓"}
            </p>
            <p
              style={{
                color: "var(--text-secondary, #94a3b8)",
                margin: "0 0 20px 0",
                fontSize: "0.85rem",
              }}
            >
              We&apos;ve sent a 6-digit OTP to{" "}
              <strong style={{ color: "#a5b4fc" }}>{email}</strong>
            </p>

            {errorMsg && <div className="login-error">{errorMsg}</div>}

            <form
              onSubmit={handleOtpSubmit}
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <input
                type="text"
                className="form-input"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setOtp(val);
                }}
                maxLength={6}
                autoFocus
                style={{
                  textAlign: "center",
                  fontSize: "1.3rem",
                  letterSpacing: "0.5em",
                  fontWeight: 700,
                }}
              />
              <button
                type="submit"
                className="login-btn"
                disabled={loading || otp.length !== 6}
              >
                {loading ? "Verifying..." : "Verify & Login"}
              </button>
            </form>

            <button
              type="button"
              className="toggle-btn"
              onClick={() => navigate("/login")}
              style={{ marginTop: "16px" }}
            >
              Back to Login
            </button>
          </>
        )}

        {/* Success state */}
        {status === "success" && (
          <>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>✅</div>
            <h2 style={{ color: "var(--text-primary, #fff)" }}>Welcome!</h2>
            <p style={{ color: "var(--text-secondary, #94a3b8)" }}>
              Redirecting to dashboard...
            </p>
          </>
        )}

        {/* Error state */}
        {status === "error" && (
          <>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>❌</div>
            <h2 style={{ color: "#ef4444", marginBottom: "0.5rem" }}>
              Sign-In Failed
            </h2>
            <p
              style={{
                color: "var(--text-secondary, #94a3b8)",
                marginBottom: "1.5rem",
              }}
            >
              {errorMsg}
            </p>
            <button className="login-btn" onClick={() => navigate("/login")}>
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleAuthCallback;







